import { supabase } from '@/shared/lib/supabase'
import type { ProfileWithMembership } from '@/shared/types/app.types'
import type { ProfileFormValues } from '@/shared/lib/validations/auth'

const ALLOWED_EMAILS = ['connect@thejacky.com']

export async function sendOtp(email: string): Promise<void> {
  if (!ALLOWED_EMAILS.includes(email.toLowerCase())) {
    throw new Error('Access is invite-only during early preview.')
  }
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: `${window.location.origin}/auth`,
    },
  })
  if (error) throw error
}

export async function verifyOtp(email: string, token: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  })
  if (error) throw error
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function fetchProfileWithMembership(userId: string): Promise<ProfileWithMembership | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      *,
      membership:memberships!inner(*)
    `)
    .eq('id', userId)
    .eq('memberships.status', 'active')
    .maybeSingle()

  if (error) throw error
  if (!data) {
    // Profile exists but no active membership — fetch profile alone
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
    if (profileError) throw profileError
    if (!profile) return null
    if (!profile.full_name) return null // Stub from trigger — treat as no profile
    return { ...profile, membership: null }
  }

  // Supabase returns membership as array (no unique constraint on member_id) — unwrap it
  const { membership: rawMembership, ...profile } = data as typeof data & { membership: unknown }
  const membership = Array.isArray(rawMembership)
    ? (rawMembership[0] as ProfileWithMembership['membership']) ?? null
    : (rawMembership as ProfileWithMembership['membership']) ?? null
  return { ...profile, membership }
}

export async function createProfile(
  userId: string,
  email: string,
  values: ProfileFormValues
): Promise<void> {
  const { error } = await supabase.from('profiles').upsert({
    id: userId,
    email,
    full_name: values.full_name,
    phone: values.phone || null,
    university: values.university,
    course: values.course || null,
    student_id: values.student_id || null,
    pdpa_consent_at: new Date().toISOString(),
  })
  if (error) throw error
}

const DEMO_ADMIN_EMAIL = 'connect@thejacky.com'

/** Session guard — seed runs only once per browser session (page load). */
let _hasSeededThisSession = false

/**
 * Auto-seed demo data for investor demos.
 * Called after profile fetch — if the user is the admin demo account,
 * seeds realistic data via RPC **once per session** to avoid wiping
 * user-created bookings on subsequent page loads.
 * Returns the updated profile if seeded, or null if skipped.
 */
export async function seedDemoDataIfNeeded(userId: string): Promise<ProfileWithMembership | null> {
  if (_hasSeededThisSession) return null

  const { data: { user } } = await supabase.auth.getUser()
  if (!user || user.email !== DEMO_ADMIN_EMAIL) return null

  const { error } = await supabase.rpc('seed_demo_data', { target_user_id: userId })
  if (error) {
    console.error('[demo] seed failed:', error.message)
    return null
  }

  _hasSeededThisSession = true

  // Re-fetch profile with new membership + admin role
  return fetchProfileWithMembership(userId)
}

export async function updateProfile(
  userId: string,
  values: Partial<Omit<ProfileFormValues, 'pdpa_consent'>>
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({
      full_name: values.full_name,
      phone: values.phone || null,
      university: values.university,
      course: values.course || null,
      student_id: values.student_id || null,
    })
    .eq('id', userId)
  if (error) throw error
}
